/**
 * Tactic badge — the coloured code chip that precedes a tactic label
 * (PROX / CRM / POI / …).
 *
 * Shared so the Campaign Grid and the CLW plan grid cannot drift: the design
 * uses one colour per tactic code across both surfaces, and a second copy of
 * this map would quietly diverge the moment either is tweaked.
 */
import React from 'react';

export const TACTIC_BADGE_STYLES = {
  prox: { bg: '#E3F2FD', fg: '#1565C0', label: 'PROX' },
  beh: { bg: '#F3E5F5', fg: '#6A1B9A', label: 'BEH' },
  '3p': { bg: '#E8F5E9', fg: '#2E7D32', label: '3P' },
  ret: { bg: '#FFF8E1', fg: '#F57F17', label: 'RET' },
  geo: { bg: '#E0F2F1', fg: '#00695C', label: 'GEO' },
  crm: { bg: '#FFF8E1', fg: '#F57F17', label: 'CRM' },
  poi: { bg: '#FBE9E7', fg: '#BF360C', label: 'POI' },
  kw: { bg: '#FFFBEB', fg: '#92400E', label: 'KW' },
  aud: { bg: '#EEF3FF', fg: '#2A4FC4', label: 'AUD' },
};

/**
 * Map a human tactic label ("Proximity-Loyalty", "Competitor POI") to a code.
 * Match order is significant and is kept exactly as the Campaign Grid had it —
 * 'geo' is tested before 'crm', so a label containing both resolves to geo.
 */
export function deriveTacticId(tacticLabel) {
  const t = String(tacticLabel || '').toLowerCase();
  if (t.includes('proximity')) return 'prox';
  if (t.includes('behavioral')) return 'beh';
  if (t.includes('3p') || t.includes('third')) return '3p';
  if (t.includes('retarget')) return 'ret';
  if (t.includes('geo')) return 'geo';
  if (t.includes('crm')) return 'crm';
  if (t.includes('competitor') || t.includes('poi')) return 'poi';
  if (t.includes('keyword')) return 'kw';
  if (t.includes('audience') || t.includes('lookalike')) return 'aud';
  return null;
}

/**
 * Chip + label. `className` names the chip's class so each surface can keep its
 * own sizing (the two grids use different type scales).
 */
export function TacticPill({ tactic, tacticId, className = 'cg-tactic-badge' }) {
  if (!tactic) return null;
  const id = tacticId || deriveTacticId(tactic);
  const style = id && TACTIC_BADGE_STYLES[id];
  if (!style) return <span>{tactic}</span>;
  return (
    <span>
      <span className={className} style={{ background: style.bg, color: style.fg }}>{style.label}</span>
      {tactic}
    </span>
  );
}
